import { authenticatedInstance } from '../index/index.service';
import type { AttendanceActionType, AttendanceActionView, AttendanceDashboardView, PrinterView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { AttendanceActionType, AttendanceActionView, AttendanceDashboardView } from 'shared-models/browser';

export type AttendanceShiftStatusView = {
  employeeId: number;
  isOpenShift: boolean;
  clockInAt: string | null;
  currentDurationMinutes: number;
};

export type ClockAttendanceActionRequest = {
  employeeId: number;
  pin: string;
  actionType: AttendanceActionType;
};

export type ManualAttendanceActionRequest = {
  employeeId: number;
  actionType: AttendanceActionType;
  actionDate: string;
};

class AttendanceService {
  async getEmployeeShiftStatus(tenantId: string, employeeId: number): Promise<AttendanceShiftStatusView> {
    try {
      const response = await authenticatedInstance.get<AttendanceShiftStatusView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/employee/${employeeId}/shift-status`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading employee shift status'));
    }
  }

  async clockAction(tenantId: string, payload: ClockAttendanceActionRequest): Promise<AttendanceActionView> {
    try {
      const response = await authenticatedInstance.post<AttendanceActionView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/clock-action`,
        payload,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to clock attendance action'));
    }
  }

  async listBillablePrinters(tenantId: string): Promise<PrinterView[]> {
    try {
      const response = await authenticatedInstance.get<PrinterView[]>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/billable-printers`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading billable printers'));
    }
  }

  async printSlip(
    tenantId: string,
    printerId: number,
    printableImageDataUrl: string,
    employeeId?: number,
  ): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/print-slip`,
        { printerId, printableImageDataUrl, ...(employeeId ? { employeeId } : {}) },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed sending attendance print'));
    }
  }

  async getDashboard(
    tenantId: string,
    employeeSession: string,
    params?: { from?: string; to?: string; employeeId?: number },
  ): Promise<AttendanceDashboardView> {
    try {
      const response = await authenticatedInstance.get<AttendanceDashboardView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/dashboard`,
        {
          params,
          headers: { 'x-employee-session': employeeSession },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading attendance dashboard'));
    }
  }

  async getSelfDashboard(
    tenantId: string,
    employeeSession: string,
    params?: { from?: string; to?: string },
  ): Promise<AttendanceDashboardView> {
    try {
      const response = await authenticatedInstance.get<AttendanceDashboardView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/self-dashboard`,
        {
          params,
          headers: { 'x-employee-session': employeeSession },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading self attendance dashboard'));
    }
  }

  async addManualAction(
    tenantId: string,
    payload: ManualAttendanceActionRequest,
    employeeSession: string,
  ): Promise<AttendanceActionView> {
    try {
      const response = await authenticatedInstance.post<AttendanceActionView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/manual-action`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed adding attendance action'));
    }
  }

  async updateAction(
    tenantId: string,
    actionId: number,
    payload: { actionType: AttendanceActionType; actionDate: string },
    employeeSession: string,
  ): Promise<AttendanceActionView> {
    try {
      const response = await authenticatedInstance.patch<AttendanceActionView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/actions/${actionId}`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating attendance action'));
    }
  }

  async updateRetroSalary(
    tenantId: string,
    employeeSession: string,
    payload: { employeeId: number; clockInAt: string[]; hourlySalary: number },
  ): Promise<void> {
    try {
      await authenticatedInstance.patch(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/retro-salary`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating retroactive shift salary'));
    }
  }

  async manualCloseOpenShift(
    tenantId: string,
    employeeId: number,
    employeeSession: string,
  ): Promise<AttendanceActionView> {
    try {
      const response = await authenticatedInstance.post<AttendanceActionView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/manual-close`,
        { employeeId },
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed closing open shift'));
    }
  }
}

export const attendanceService = new AttendanceService();
